import React, { useState, useEffect } from "react";
import Modal from "../../../../components/Modal";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../../../components/ui/input-otp";
import { toast } from "react-toastify";
import {
  sendAadharOtpAction,
  verifyAadharOtpAction,
  getDocumentNameForField,
} from "../../../../actions/aadharOtpActions";
import "./index.scss";

const OtpVerificationModal = ({
  isOpen,
  onClose,
  onVerifySuccess,
  adminEmail,
  fieldName = "Aadhar Number",
  fieldKey = "",
  entityPrimaryId = 0,
}) => {
  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setOtp("");
      setOtpSent(false);
      setTimer(0);
    }
  }, [isOpen]);

  const handleSendOtp = async () => {
    setIsSendingOtp(true);

    try {
      const response = await sendAadharOtpAction(adminEmail);

      if (response.success) {
        setOtpSent(true);
        setTimer(60); // 60 seconds cooldown
        toast.success(`OTP sent to ${adminEmail}`, {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error(
          response.message || "Failed to send OTP. Please try again.",
          {
            position: "top-right",
            autoClose: 3000,
          },
        );
      }
    } catch (error) {
      toast.error("Failed to send OTP. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter a 6-digit OTP", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsVerifying(true);

    try {
      // Get the document name for the API
      const documentName = getDocumentNameForField(fieldKey);

      const response = await verifyAadharOtpAction({
        entityPrimaryId,
        documentName,
        email: adminEmail,
        otp,
      });

      if (response.success) {
        toast.success("OTP verified successfully!", {
          position: "top-right",
          autoClose: 2000,
        });
        // Pass the original value back to parent
        onVerifySuccess(response.originalValue);
        onClose();
      } else {
        toast.error(response.message || "Invalid OTP. Please try again.", {
          position: "top-right",
          autoClose: 3000,
        });
        setOtp("");
      }
    } catch (error) {
      toast.error("Failed to verify OTP. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = () => {
    if (timer > 0) return;
    setOtp("");
    handleSendOtp();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Verify OTP to View ${fieldName}`}
      size="medium"
    >
      <div className="otp-verification-modal">
        <div className="otp-info">
          <div className="info-icon">🔒</div>
          <p className="info-text">
            For security reasons, viewing sensitive {fieldName} requires OTP
            verification.
          </p>
          <p className="info-email">
            OTP will be sent to: <strong>{adminEmail}</strong>
          </p>
        </div>

        {!otpSent ? (
          <div className="otp-send-section">
            <button
              type="button"
              className="btn-send-otp"
              onClick={handleSendOtp}
              disabled={isSendingOtp}
            >
              {isSendingOtp ? "Sending OTP..." : "Send OTP"}
            </button>
          </div>
        ) : (
          <div className="otp-verify-section">
            <label className="otp-label">Enter 6-digit OTP</label>
            <div className="otp-input-wrapper">
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={(value) => setOtp(value)}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="otp-actions">
              <button
                type="button"
                className="btn-verify-otp"
                onClick={handleVerifyOtp}
                disabled={isVerifying || otp.length !== 6}
              >
                {isVerifying ? "Verifying..." : "Verify OTP"}
              </button>
            </div>

            <div className="otp-resend">
              {timer > 0 ? (
                <p className="resend-timer">
                  Resend OTP in <strong>{timer}s</strong>
                </p>
              ) : (
                <button
                  type="button"
                  className="btn-resend-otp"
                  onClick={handleResendOtp}
                >
                  Resend OTP
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default OtpVerificationModal;
